import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { Frame, Ruler, Save, RotateCcw, Sparkles, AlertTriangle, Printer, Layers, Search, ImagePlus, X, Lock, FileDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { TooltipProvider } from "./ui/tooltip";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { FramePreview } from "./FramePreview";
import { CrossSection } from "./CrossSection";
import { SavedCalculations } from "./SavedCalculations";
import { CutSheet } from "./CutSheet";
import { Help } from "./Help";
import { CostInputs } from "./CostInputs";
import { Seo } from "./Seo";
import { useAuth } from "../context/AuthContext";
import { http } from "../lib/api";
import {
  RULES, RULE_NOTES, LAYER_OPTS, GLASS_OPTS, PLY_OPTS, BACKING_OPTS, SPACER_TYPE_OPTS, SPACER_DEPTH_OPTS, PAPER_OPTS,
  PRESETS, MOULDING_COLORS, MAT_COLORS_OPTS, HELP, MM_PER_INCH, fmt, convertVal, calculate, computeCosts, fmtMoney,
} from "../lib/framing";

const DEFAULT_FORM = {
  unit: "mm",
  reference: "",
  mode: "solve_frame",
  rule: "weighted_bottom",
  mat_width: "60",
  weighted_factor: "1.3",
  mat_layers: "1",
  under_mat_reveal: "6",
  third_mat_reveal: "5",
  painting_width: "400",
  painting_height: "500",
  paper_mm: "0.35",
  outer_width: "600",
  outer_height: "720",
  moulding_face_width: "25",
  rebate_width: "8",
  rebate_depth: "20",
  glazing_clearance: "0.5",
  mat_outer_allowance: "0.2",
  moulding_allowance_pct: "5",
  currency: "$",
  mat_cost_unit: "", mat_cost_price: "",
  moulding_cost_unit: "", moulding_cost_price: "",
  glazing_cost_unit: "", glazing_cost_price: "",
  glazing_custom_w: "", glazing_custom_h: "",
  spacer_cost_unit: "", spacer_cost_price: "",
  mat_top: "60",
  mat_bottom: "78",
  mat_left: "60",
  mat_right: "60",
  depth_calc: false,
  glass_depth_mm: "3",
  ply_depth_mm: "1.4",
  backing_depth_mm: "2",
  spacer_depth_mm: "0",
  spacer_type: "none",
  moulding_color: "#C9A66B",
  mat_color_1: "#FBFBF8",
  mat_color_2: "#3A3A3A",
  mat_color_3: "#8E8698",
  artwork_image_url: "",
};

const MEASURE_KEYS = [
  "mat_width", "under_mat_reveal", "third_mat_reveal", "painting_width", "painting_height",
  "outer_width", "outer_height", "moulding_face_width", "rebate_width",
  "rebate_depth", "mat_top", "mat_bottom", "mat_left", "mat_right", "glazing_clearance",
];

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const mmConst = (v, u) => String(Math.round((u === "in" ? v * MM_PER_INCH : v) * 100) / 100);

// Sections tracked for the anonymous pre-trial adjustment limit.
const TRACKED_KEYS = [
  "mode", "painting_width", "painting_height", "outer_width", "outer_height",
  "mat_layers", "rule", "mat_width", "weighted_factor", "under_mat_reveal", "third_mat_reveal",
  "mat_top", "mat_bottom", "mat_left", "mat_right", "mat_color_1", "mat_color_2", "mat_color_3",
  "moulding_face_width", "rebate_width", "rebate_depth", "moulding_allowance_pct", "moulding_color",
  "depth_calc", "paper_mm", "glass_depth_mm", "ply_depth_mm", "backing_depth_mm", "spacer_depth_mm",
  "spacer_type", "glazing_clearance", "artwork_image_url", "mat_outer_allowance",
];

function getFingerprint() {
  let fp = localStorage.getItem("fb_fp");
  if (fp) return fp;
  const seed = [navigator.userAgent, navigator.language, window.screen?.width, window.screen?.height, window.screen?.colorDepth, new Date().getTimezoneOffset()].join("|");
  let h = 0;
  for (let i = 0; i < seed.length; i++) { h = (h << 5) - h + seed.charCodeAt(i); h |= 0; }
  fp = `fp_${Math.abs(h).toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem("fb_fp", fp);
  return fp;
}

const Segmented = ({ value, options, onChange, testidPrefix }) => (
  <div className="inline-flex flex-wrap rounded-lg border border-border bg-secondary p-1">
    {options.map((o) => (
      <button
        key={o.value}
        data-testid={`${testidPrefix}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          value === o.value ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Field = ({ label, k, form, unit, onChange, testid, help, onBlur, min, max, step }) => {
  const clamp = () => {
    if (min === undefined && max === undefined) return;
    let n = parseFloat(form[k]);
    if (isNaN(n)) return;
    if (min !== undefined && n < Number(min)) n = Number(min);
    if (max !== undefined && n > Number(max)) n = Number(max);
    if (String(n) !== String(form[k])) onChange(k, String(n));
  };
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
        {label}{help ? <Help text={help} testid={`help-${k}`} /> : null}
      </label>
      <div className="relative">
        <Input data-testid={testid} type="number" inputMode="decimal" min={min} max={max} step={step} value={form[k]} onChange={(e) => onChange(k, e.target.value)} onBlur={onBlur || clamp} className="pr-12 font-mono" />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
};

const ColorSwatches = ({ value, options, onChange, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((c) => (
      <button
        key={c.hex}
        type="button"
        title={c.name}
        data-testid={`${testidPrefix}-${c.hex.replace("#", "")}`}
        onClick={() => onChange(c.hex)}
        className={`h-7 w-7 rounded-full border transition-transform hover:scale-110 ${value === c.hex ? "ring-2 ring-primary ring-offset-2" : "border-border"}`}
        style={{ background: c.hex }}
      />
    ))}
  </div>
);

const MatColorSelect = ({ label, k, form, onChange, testid }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</label>
    <Select value={form[k]} onValueChange={(v) => onChange(k, v)}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">
        {MAT_COLORS_OPTS.map((c) => (
          <SelectItem key={c.hex} value={c.hex}>
            <span className="flex items-center gap-2">
              <span className="inline-block h-3.5 w-3.5 rounded-full border border-border" style={{ background: c.hex }} />
              {c.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const DepthSelect = ({ label, k, form, onChange, options, testid }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</label>
    <Select value={form[k]} onValueChange={(v) => onChange(k, v)}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.mm} value={String(o.mm)}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const SectionHead = ({ icon: Icon, title, help, helpTestid }) => (
  <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-bold">
    <Icon className="h-4 w-4 text-primary" /> {title}
    <Help text={help} testid={helpTestid} />
  </h2>
);

const ResultRow = ({ label, value, unit, testid, strong, help }) => (
  <div data-testid={testid} className="flex items-center justify-between border-b border-border py-2">
    <span className={`flex items-center gap-1 text-sm ${strong ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
      {label}{help ? <Help text={help} testid={`help-${testid}`} /> : null}
    </span>
    <span className={`font-mono text-sm ${strong ? "font-semibold text-primary" : "text-foreground"}`}>
      {value} <span className="text-xs text-muted-foreground">{unit}</span>
    </span>
  </div>
);

export default function FrameCalculator() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const debounceRef = useRef(null);
  const unit = form.unit;

  const { user, access, isSubscribed, isTrial, isExpired, trialDaysLeft } = useAuth();
  const navigate = useNavigate();
  const goPricing = () => navigate("/pricing");
  const goTrial = () => navigate("/login", { state: { from: "/calculator" } });
  const canExport = isSubscribed || isTrial;
  const anonymous = access === "anonymous";

  const [usageLocked, setUsageLocked] = useState(false);
  const [softOpen, setSoftOpen] = useState(false);
  const softShownRef = useRef(false);
  const prevTrackedRef = useRef(null);
  const fpRef = useRef(null);

  const buildInput = useCallback((f) => {
    const d = (mm) => (f.unit === "in" ? toNum(mm) / MM_PER_INCH : toNum(mm));
    return {
      unit: f.unit, mode: f.mode, rule: f.rule,
      mat_width: toNum(f.mat_width), weighted_factor: toNum(f.weighted_factor) || 1,
      mat_layers: parseInt(f.mat_layers, 10) || 0,
      under_mat_reveal: toNum(f.under_mat_reveal), third_mat_reveal: toNum(f.third_mat_reveal),
      painting_width: toNum(f.painting_width), painting_height: toNum(f.painting_height), painting_depth: d(f.paper_mm),
      outer_width: toNum(f.outer_width), outer_height: toNum(f.outer_height),
      moulding_face_width: toNum(f.moulding_face_width), rebate_width: toNum(f.rebate_width), rebate_depth: toNum(f.rebate_depth),
      glazing_clearance: toNum(f.glazing_clearance), moulding_allowance_pct: toNum(f.moulding_allowance_pct),
      mat_outer_allowance: toNum(f.mat_outer_allowance),
      currency: f.currency,
      mat_cost_unit: f.mat_cost_unit, mat_cost_price: toNum(f.mat_cost_price),
      moulding_cost_unit: f.moulding_cost_unit, moulding_cost_price: toNum(f.moulding_cost_price),
      glazing_cost_unit: f.glazing_cost_unit, glazing_cost_price: toNum(f.glazing_cost_price),
      glazing_custom_w: toNum(f.glazing_custom_w), glazing_custom_h: toNum(f.glazing_custom_h),
      spacer_cost_unit: f.spacer_cost_unit, spacer_cost_price: toNum(f.spacer_cost_price),
      mat_top: toNum(f.mat_top), mat_bottom: toNum(f.mat_bottom), mat_left: toNum(f.mat_left), mat_right: toNum(f.mat_right),
      depth_calc: f.depth_calc,
      glass_depth: d(f.glass_depth_mm), matboard_ply_depth: d(f.ply_depth_mm),
      backing_depth: d(f.backing_depth_mm), spacer_depth: d(f.spacer_depth_mm), spacer_type: f.spacer_type,
      moulding_color: f.moulding_color, mat_color_1: f.mat_color_1, mat_color_2: f.mat_color_2, mat_color_3: f.mat_color_3, artwork_image_url: f.artwork_image_url,
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setResult(await calculate(buildInput(form))); } catch (e) { console.error(e); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [form, buildInput]);

  const loadSaved = useCallback(async () => {
    if (!user) { setSaved([]); return; }
    try { const { data } = await http.get(`/calculations`); setSaved(data); }
    catch (e) { console.error(e); }
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  // ---- Anonymous pre-trial adjustment tracking (fingerprint + IP, 24h reset) ----
  const applyUsage = useCallback((d) => {
    setUsageLocked(d.locked);
    if (d.soft && !d.locked && !softShownRef.current) {
      softShownRef.current = true;
      setSoftOpen(true);
    }
  }, []);

  useEffect(() => {
    if (access !== "anonymous") { setUsageLocked(false); return; }
    fpRef.current = getFingerprint();
    http.post("/usage/track", { fingerprint: fpRef.current, increment: false })
      .then(({ data }) => applyUsage(data)).catch(() => {});
  }, [access, applyUsage]);

  useEffect(() => {
    const snap = TRACKED_KEYS.map((k) => String(form[k])).join("\u00ac");
    if (prevTrackedRef.current === null) { prevTrackedRef.current = snap; return; }
    if (snap === prevTrackedRef.current) return;
    prevTrackedRef.current = snap;
    if (access !== "anonymous" || usageLocked) return;
    const t = setTimeout(() => {
      http.post("/usage/track", { fingerprint: fpRef.current || getFingerprint(), increment: true })
        .then(({ data }) => applyUsage(data)).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [form, access, usageLocked, applyUsage]);

  const onChange = (k, v) => {
    if (usageLocked) {
      toast.message("Free preview limit reached", { description: "Start your 7-day free trial to keep refining." });
      return;
    }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onUnitChange = (newUnit) => {
    if (newUnit === unit || usageLocked) return;
    setForm((f) => {
      const next = { ...f, unit: newUnit };
      MEASURE_KEYS.forEach((k) => { next[k] = String(convertVal(f[k], unit, newUnit)); });
      return next;
    });
  };

  const onUploadArtwork = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (usageLocked) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await http.post(`/upload-artwork`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange("artwork_image_url", data.url);
      toast.success("Artwork image added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyPreset = (val) => {
    if (usageLocked) return;
    const [w, h] = val.split("x").map(Number);
    const conv = (mm) => (unit === "in" ? Math.round((mm / MM_PER_INCH) * 1000) / 1000 : mm);
    setForm((f) => ({ ...f, painting_width: String(conv(w)), painting_height: String(conv(h)) }));
  };

  const resetForm = () => { if (usageLocked) return; setForm(DEFAULT_FORM); toast.success("Reset to defaults"); };

  const promptTrialOrSubscribe = (what) => {
    if (isExpired) {
      toast.message("Your free trial has ended", { description: `Subscribe to ${what} your cut sheets and keep your projects.` });
      goPricing();
    } else {
      toast.message("Start your 7-day free trial", { description: "Start your 7-day free trial to save or export your progress, continue refining and access precise cut sheets." });
      goTrial();
    }
  };

  const onSaveClick = () => {
    if (!user) { goTrial(); return; }
    if (isExpired) { toast.message("Your free trial has ended", { description: "Subscribe to save and keep your projects." }); goPricing(); return; }
    setSaveOpen(true);
    if (!saveName.trim()) setSaveName(form.reference);
  };

  const onPrint = () => {
    if (canExport) { window.print(); return; }
    promptTrialOrSubscribe("print");
  };

  const exportPdf = () => {
    if (!canExport) { promptTrialOrSubscribe("export"); return; }
    const r = result;
    if (!r) return;
    const u = unit;
    const dim = (w, h) => `${fmt(w, u)} × ${fmt(h, u)} ${u}`;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const M = 48;
    let y = M;
    const page = doc.internal.pageSize.getWidth();
    const line = (label, value, opts = {}) => {
      if (y > 780) { doc.addPage(); y = M; }
      doc.setFont("helvetica", opts.bold ? "bold" : "normal");
      doc.setFontSize(opts.size || 11);
      doc.setTextColor(opts.muted ? 120 : 30);
      doc.text(String(label), M, y);
      if (value !== undefined) {
        doc.setTextColor(30);
        doc.text(String(value), page - M, y, { align: "right" });
      }
      y += opts.gap || 18;
    };
    const heading = (t) => { y += 8; line(t, undefined, { bold: true, size: 9, muted: true }); doc.setDrawColor(220); doc.line(M, y - 12, page - M, y - 12); };

    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(20);
    doc.text("Framer's Bench — Cut Sheet", M, y); y += 22;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120);
    doc.text(`${form.reference || "Untitled job"}  ·  ${new Date().toLocaleDateString()}  ·  units: ${u}`, M, y); y += 10;

    heading("Frame");
    line("Outer frame", dim(r.outer_width, r.outer_height), { bold: true });
    line("Visible opening", dim(r.visible_opening_width, r.visible_opening_height));
    if (r.moulding_length_total) {
      line(`Moulding length (incl. ${fmt(r.moulding_allowance_pct, u)}% allowance)`, `${fmt(r.moulding_length_total, u)} ${u}`);
      line("Moulding perimeter", `${fmt(r.moulding_perimeter, u)} ${u}`, { muted: true });
    }
    if (r.depth_calc) line("Total stack depth", `${fmt(r.total_depth, u)} ${u}`);

    heading("Cut sizes");
    line("Outer frame (mitred 45°, 2 each)", dim(r.outer_width, r.outer_height));
    if (r.spacer_type && r.spacer_type !== "none") {
      if (r.spacer_type === "matboard" || r.spacer_type === "foam") {
        line("Spacer outer", dim(r.spacer_outer_width, r.spacer_outer_height));
        line("Spacer inner", dim(r.spacer_inner_width, r.spacer_inner_height));
      } else if (r.spacer_type === "plastic") {
        line("Spacer top/bottom (×2)", `${fmt(r.spacer_tb_length, u)} ${u}`);
        line("Spacer left/right (×2)", `${fmt(r.spacer_lr_length, u)} ${u}`);
      } else if (r.spacer_type === "wood") {
        line("Spacer lengths (mitred, ×2 each)", dim(r.spacer_wood_length_a, r.spacer_wood_length_b));
      }
    }
    line("Glazing", dim(r.glass_cut_width, r.glass_cut_height));
    if (layers > 0) line("Matboard", dim(r.total_mat_width, r.total_mat_height));
    line("Backing", dim(r.backing_cut_width, r.backing_cut_height));

    if (mats.length > 0) {
      heading("Border cuts (matboard edge → cut line)");
      mats.forEach((m) => {
        line(m.name, `T ${fmt(m.cut_top, u)}  ·  B ${fmt(m.cut_bottom, u)}  ·  L ${fmt(m.cut_left, u)}  ·  R ${fmt(m.cut_right, u)}`);
      });
    }

    const cd = computeCosts(form, r, u);
    if (cd.rows.length > 0) {
      heading("Component costs");
      cd.rows.forEach((row) => line(row.label, fmtMoney(row.cost, cd.currency)));
      line("Total framing cost", fmtMoney(cd.total, cd.currency), { bold: true });
    }

    doc.setFontSize(9); doc.setTextColor(150);
    doc.text("Generated by Framer's Bench · framersbench.com", M, 812);
    doc.save(`framers-bench-${(form.reference || "cut-sheet").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`);
  };

  const onDepthToggle = (v) => {
    onChange("depth_calc", v);
  };

  const doSave = async (overwrite = false) => {
    const name = saveName.trim();
    if (!name) { toast.error("Please name this calculation"); return; }
    try {
      await http.post(`/calculations`, { name, input: buildInput(form), overwrite });
      toast.success(`Saved “${name}”`); setSaveName(""); setSaveOpen(false); setDupOpen(false); loadSaved();
    } catch (e) {
      if (e?.response?.status === 409) { setSaveOpen(false); setDupOpen(true); return; }
      toast.error(e?.response?.data?.detail || "Could not save calculation");
    }
  };

  const exportLibrary = async () => {
    try {
      const res = await http.get("/calculations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "framers-bench-library.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Library exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export library");
    }
  };

  const onLoad = (c) => {
    const i = c.input;
    const u = i.unit;
    setForm({
      unit: u, reference: c.name, mode: i.mode, rule: i.rule,
      mat_width: String(i.mat_width), weighted_factor: String(i.weighted_factor),
      mat_layers: String(i.mat_layers ?? 1), under_mat_reveal: String(i.under_mat_reveal ?? 6), third_mat_reveal: String(i.third_mat_reveal ?? 5),
      painting_width: String(i.painting_width), painting_height: String(i.painting_height),
      outer_width: String(i.outer_width), outer_height: String(i.outer_height),
      moulding_face_width: String(i.moulding_face_width), rebate_width: String(i.rebate_width), rebate_depth: String(i.rebate_depth ?? 0),
      glazing_clearance: String(i.glazing_clearance ?? 0.5), moulding_allowance_pct: String(i.moulding_allowance_pct ?? 5),
      mat_outer_allowance: String(i.mat_outer_allowance ?? 0.2),
      currency: i.currency ?? "$",
      mat_cost_unit: i.mat_cost_unit ?? "", mat_cost_price: i.mat_cost_price ? String(i.mat_cost_price) : "",
      moulding_cost_unit: i.moulding_cost_unit ?? "", moulding_cost_price: i.moulding_cost_price ? String(i.moulding_cost_price) : "",
      glazing_cost_unit: i.glazing_cost_unit ?? "", glazing_cost_price: i.glazing_cost_price ? String(i.glazing_cost_price) : "",
      glazing_custom_w: i.glazing_custom_w ? String(i.glazing_custom_w) : "", glazing_custom_h: i.glazing_custom_h ? String(i.glazing_custom_h) : "",
      spacer_cost_unit: i.spacer_cost_unit ?? "", spacer_cost_price: i.spacer_cost_price ? String(i.spacer_cost_price) : "",
      mat_top: String(i.mat_top), mat_bottom: String(i.mat_bottom), mat_left: String(i.mat_left), mat_right: String(i.mat_right),
      depth_calc: !!i.depth_calc,
      paper_mm: mmConst(i.painting_depth ?? 0, u),
      glass_depth_mm: mmConst(i.glass_depth ?? 0, u), ply_depth_mm: mmConst(i.matboard_ply_depth ?? 0, u),
      backing_depth_mm: mmConst(i.backing_depth ?? 0, u), spacer_depth_mm: mmConst(i.spacer_depth ?? 0, u),
      spacer_type: i.spacer_type ?? "none",
      moulding_color: i.moulding_color ?? "#C9A66B",
      mat_color_1: i.mat_color_1 ?? i.mat_color ?? "#FBFBF8",
      mat_color_2: i.mat_color_2 ?? "#3A3A3A",
      mat_color_3: i.mat_color_3 ?? "#8E8698",
      artwork_image_url: i.artwork_image_url ?? "",
    });
    toast.success(`Loaded “${c.name}”`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    try { await http.delete(`/calculations/${id}`); setSaved((s) => s.filter((c) => c.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const onDuplicate = async (c) => {
    if (!isSubscribed) { toast.message("Duplicating projects is a subscriber feature", { description: "Subscribe to duplicate saved projects." }); goPricing(); return; }
    try { await http.post(`/calculations/${c.id}/duplicate`); toast.success(`Duplicated “${c.name}”`); loadSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
  };

  const solvingFrame = form.mode === "solve_frame";
  const layers = parseInt(form.mat_layers, 10);
  const mats = result?.mat_cuts || [];
  const faceNum = toNum(form.moulding_face_width);
  const borderNum = toNum(form.mat_width);
  const borderTooSmall = form.rule !== "custom" && layers > 0 && borderNum > 0 && faceNum > 0 && borderNum < 1.5 * faceNum;
  const onBorderBlur = () => {
    const b = toNum(form.mat_width);
    if (faceNum > 0 && b > 0 && b < faceNum) {
      onChange("mat_width", String(faceNum));
      toast.warning(`Border width can't be less than the moulding face width (${fmt(faceNum, unit)} ${unit}).`);
    }
  };
  const q = query.trim().toLowerCase();
  const filteredSaved = q ? saved.filter((c) => c.name.toLowerCase().includes(q)) : saved;

  const clearanceMax = toNum(form.rebate_width) - (unit === "in" ? 1.5 / MM_PER_INCH : 1.5);
  const clearanceOver = toNum(form.glazing_clearance) > clearanceMax;
  const matAllowMax = Math.max(toNum(form.rebate_width) / 2, 0);
  const matAllowOver = toNum(form.mat_outer_allowance) > matAllowMax;
  const costData = computeCosts(form, result, unit);

  return (
    <TooltipProvider>
      <div className="bg-background">
        <Seo title="Frame & Mat Calculator" path="/calculator"
          description="Free online frame and mat calculator. Enter your artwork or frame size to get exact mat borders, matboard cut sizes, glass, backing and outer frame dimensions in mm or inches." />
        <div className="border-b border-border bg-white/70">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-lg font-extrabold tracking-tight">Frame &amp; Mat Calculator</h1>
              {anonymous && <Badge variant="secondary" data-testid="free-badge">Free preview</Badge>}
              {isTrial && <Badge data-testid="trial-badge">Trial · {trialDaysLeft}d left</Badge>}
              {isExpired && <Badge variant="secondary" data-testid="expired-badge">Trial ended</Badge>}
              {isSubscribed && <Badge data-testid="plan-badge-calc">Subscribed</Badge>}
            </div>
            <Segmented value={unit} onChange={onUnitChange} testidPrefix="toggle-unit"
              options={[{ value: "mm", label: "mm" }, { value: "in", label: "inches" }]} />
          </div>
        </div>

        {!isSubscribed && !(isTrial && trialDaysLeft <= 3) && (
          <div className="border-b border-primary/20 bg-primary/5" data-testid="upgrade-banner">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:px-6 lg:px-8">
              <p className="text-foreground/80">
                <Lock className="mr-1.5 inline h-3.5 w-3.5 text-primary" />
                {anonymous
                  ? "Every calculation on one single, easy-to-read page. Start your 7-day free trial (no card needed) to save, export and keep refining."
                  : isTrial
                  ? `Free trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left. Framing materials are expensive: subscribe to keep your projects and unlock the materials cost calculator.`
                  : "Your free trial has ended. Subscribe now to keep your account — unsubscribed trial accounts are closed and their projects removed shortly after the trial ends."}
              </p>
              <Button size="sm" onClick={anonymous ? goTrial : goPricing} data-testid="banner-upgrade-btn">
                {anonymous ? "Start free trial" : "Subscribe"}
              </Button>
            </div>
          </div>
        )}

        <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-12 lg:px-8">
          {/* Inputs */}
          <section className="lg:col-span-5 xl:col-span-4">
            <div className="relative rounded-xl border border-border bg-card p-5">
              {usageLocked && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-xl bg-background/90 p-6 text-center backdrop-blur-sm" data-testid="usage-lock-overlay">
                  <Lock className="h-7 w-7 text-primary" />
                  <h3 className="font-heading text-lg font-bold">Enjoying the calculator?</h3>
                  <p className="max-w-xs text-sm text-muted-foreground">Start your 7-day free trial to save your progress, continue refining and access precise cut sheets.</p>
                  <Button onClick={goTrial} data-testid="usage-lock-cta">Start your free trial</Button>
                  <p className="text-xs text-muted-foreground">Your last result stays visible on the right.</p>
                </div>
              )}
              {/* Reference */}
              <div className="mb-5">
                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Job reference <Help text={HELP.reference} testid="help-reference" />
                </label>
                <Input data-testid="input-reference" value={form.reference} onChange={(e) => onChange("reference", e.target.value)} placeholder="e.g. Smith commission — A2 print" />
              </div>

              {/* Mode */}
              <div className="mb-5">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Calculate <Help text={HELP.mode} testid="help-mode" />
                </p>
                <Segmented value={form.mode} onChange={(v) => onChange("mode", v)} testidPrefix="toggle-mode"
                  options={[{ value: "solve_frame", label: "Frame size" }, { value: "solve_painting", label: "Artwork size" }]} />
              </div>

              {/* Known dims */}
              <SectionHead icon={Ruler} title={solvingFrame ? "Artwork dimensions" : "Outer frame dimensions"} help={HELP.painting} helpTestid="help-painting" />
              {solvingFrame && (
                <div className="mb-3">
                  <Select onValueChange={applyPreset}>
                    <SelectTrigger data-testid="select-preset"><SelectValue placeholder="Standard size preset (optional)…" /></SelectTrigger>
                    <SelectContent>
                      {PRESETS.map((g) => (
                        <SelectGroup key={g.group}>
                          <SelectLabel>{g.group}</SelectLabel>
                          {g.items.map((it) => (
                            <SelectItem key={it.label} value={`${it.w}x${it.h}`}>{it.label}</SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="mb-6 grid grid-cols-2 gap-3">
                {solvingFrame ? (
                  <>
                    <Field label="Width" k="painting_width" form={form} unit={unit} onChange={onChange} testid="input-painting-width" />
                    <Field label="Height" k="painting_height" form={form} unit={unit} onChange={onChange} testid="input-painting-height" />
                  </>
                ) : (
                  <>
                    <Field label="Outer width" k="outer_width" form={form} unit={unit} onChange={onChange} testid="input-outer-width" />
                    <Field label="Outer height" k="outer_height" form={form} unit={unit} onChange={onChange} testid="input-outer-height" />
                  </>
                )}
              </div>

              {/* Artwork image */}
              <div className="mb-6">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Artwork image (optional)</label>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onUploadArtwork} className="hidden" data-testid="input-artwork-file" />
                {form.artwork_image_url ? (
                  <div className="flex items-center gap-3">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${form.artwork_image_url}`} alt="artwork" className="h-14 w-14 rounded-md border border-border object-cover" />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="replace-artwork-btn">Replace</Button>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => onChange("artwork_image_url", "")} data-testid="remove-artwork-btn"><X className="mr-1 h-4 w-4" />Remove</Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-artwork-btn">
                    <ImagePlus className="mr-2 h-4 w-4" /> {uploading ? "Uploading…" : "Upload artwork image"}
                  </Button>
                )}
              </div>

              {/* Mat aesthetic */}
              <SectionHead icon={Sparkles} title="Matboard aesthetic" help={HELP.mat} helpTestid="help-mat" />
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Mat layers</p>
              <div className="mb-4">
                <Segmented value={form.mat_layers} onChange={(v) => onChange("mat_layers", v)} testidPrefix="toggle-layers" options={LAYER_OPTS} />
              </div>

              {layers === 0 ? (
                <p className="mb-6 rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
                  No matboard — the artwork sits directly inside the moulding (optionally with spacers).
                </p>
              ) : (
                <>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {RULES.map((r) => (
                      <button key={r.value} data-testid={`toggle-rule-${r.value}`} onClick={() => onChange("rule", r.value)}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                          form.rule === r.value ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-muted-foreground hover:text-foreground"
                        }`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <p className="mb-4 text-xs leading-relaxed text-muted-foreground">{RULE_NOTES[form.rule]}</p>

                  {form.rule === "custom" ? (
                    <div className="mb-4 grid grid-cols-2 gap-3">
                      <Field label="Top reveal" k="mat_top" form={form} unit={unit} onChange={onChange} testid="input-mat-top" />
                      <Field label="Bottom reveal" k="mat_bottom" form={form} unit={unit} onChange={onChange} testid="input-mat-bottom" />
                      <Field label="Left reveal" k="mat_left" form={form} unit={unit} onChange={onChange} testid="input-mat-left" />
                      <Field label="Right reveal" k="mat_right" form={form} unit={unit} onChange={onChange} testid="input-mat-right" />
                    </div>
                  ) : (
                    <div className="mb-3 grid grid-cols-2 gap-3">
                      <Field label="Border width" k="mat_width" form={form} unit={unit} onChange={onChange} testid="input-mat-width"
                        help="Border width refers to the primary (front) visible matboard width." onBlur={onBorderBlur} min={faceNum || undefined} />
                      {form.rule === "weighted_bottom" && (
                        <Field label="Bottom factor" k="weighted_factor" form={form} unit="×" onChange={onChange} testid="input-weighted-factor" />
                      )}
                    </div>
                  )}

                  {borderTooSmall && (
                    <div data-testid="border-width-warning" className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>Your artwork may appear 'stuffed' or your matboard may command too much attention if your visible matboard is less than 1.5 times the width of your frame moulding.</span>
                    </div>
                  )}

                  <div className="mb-6">
                    <MatColorSelect label={layers >= 2 ? "Top matboard colour" : "Matboard colour"} k="mat_color_1" form={form} onChange={onChange} testid="select-mat-color-1" />
                  </div>

                  {layers >= 2 && (
                    <>
                      <div className="mb-3">
                        <Field label="Under-mat reveal" k="under_mat_reveal" form={form} unit={unit} onChange={onChange} testid="input-under-mat-reveal" />
                      </div>
                      <div className="mb-6">
                        <MatColorSelect label="Under-mat colour" k="mat_color_2" form={form} onChange={onChange} testid="select-mat-color-2" />
                      </div>
                    </>
                  )}

                  {layers >= 3 && (
                    <>
                      <div className="mb-3">
                        <Field label="3rd-mat reveal" k="third_mat_reveal" form={form} unit={unit} onChange={onChange} testid="input-third-mat-reveal" />
                      </div>
                      <div className="mb-6">
                        <MatColorSelect label="3rd-mat colour" k="mat_color_3" form={form} onChange={onChange} testid="select-mat-color-3" />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Moulding */}
              <SectionHead icon={Frame} title="Moulding profile" help={HELP.moulding} helpTestid="help-moulding" />
              <div className="mb-2 grid grid-cols-2 gap-3">
                <Field label="Face width" k="moulding_face_width" form={form} unit={unit} onChange={onChange} testid="input-moulding-face-width" />
                <Field label="Rebate width" k="rebate_width" form={form} unit={unit} onChange={onChange} testid="input-rebate-width" />
                <Field label="Rebate depth" k="rebate_depth" form={form} unit={unit} onChange={onChange} testid="input-rebate-depth" />
                <Field label="Cutting allowance" k="moulding_allowance_pct" form={form} unit="%" onChange={onChange} testid="input-moulding-allowance" min="0" max="10" step="1"
                  help="A moulding cutting allowance adds a selected percentage of the perimeter of the frame to account for saw kerf and trial cuts. This is particularly useful to calculate the true cost of mouldings." />
                <div>
                  <Field label="Mat outer allowance" k="mat_outer_allowance" form={form} unit={unit} onChange={onChange} testid="input-mat-outer-allowance" min="0" max={matAllowMax} step="0.1"
                    help="The matboard and backing are cut this much smaller than the rebate opening on each side (default 0.2 mm) so they drop into the frame without binding. Capped at half the rebate width." />
                  {matAllowOver && (
                    <p className="mt-1 text-xs font-medium text-destructive" data-testid="mat-allowance-warning">
                      Keep the allowance at or below half the rebate width ({fmt(matAllowMax, unit)} {unit}).
                    </p>
                  )}
                </div>
              </div>

              {/* Depth in frame */}
              <div className="mb-2 mt-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Moulding colour</label>
                <ColorSwatches value={form.moulding_color} options={MOULDING_COLORS} onChange={(v) => onChange("moulding_color", v)} testidPrefix="moulding-color" />
              </div>

              <div className="mt-6 flex items-center justify-between rounded-lg border border-border bg-secondary/50 p-3">
                <span className="flex items-center gap-1.5 font-heading text-base font-bold">
                  <Layers className="h-4 w-4 text-primary" /> Frame stack depth
                  <Help text={HELP.depth} testid="help-depth" />
                </span>
                <Switch data-testid="toggle-depth-calc" checked={form.depth_calc} onCheckedChange={onDepthToggle} />
              </div>

              {form.depth_calc && (
                <div className="mt-4 grid grid-cols-1 gap-3">
                  <DepthSelect label="Artwork paper weight" k="paper_mm" form={form} onChange={onChange} options={PAPER_OPTS} testid="select-paper" />
                  <DepthSelect label="Glazing" k="glass_depth_mm" form={form} onChange={onChange} options={GLASS_OPTS} testid="select-glass" />
                  {form.glass_depth_mm !== "0" && (
                    <div>
                      <Field label="Glazing clearance" k="glazing_clearance" form={form} unit={unit} onChange={onChange} testid="input-glazing-clearance" min="0" max={Math.max(clearanceMax, 0)} step="0.1"
                        help="The glazing is cut smaller than the mat/backing by this amount on each side (default 0.5 mm) so it fits comfortably, allows for movement and lets air flow through the frame. Capped at 1.5 mm less than the rebate width." />
                      {clearanceOver && (
                        <p className="mt-1 text-xs font-medium text-destructive" data-testid="glazing-clearance-warning">
                          The glazing edge risks being visible or falling out of the frame moulding if any higher.
                        </p>
                      )}
                    </div>
                  )}
                  <DepthSelect label="Matboard thickness (per mat)" k="ply_depth_mm" form={form} onChange={onChange} options={PLY_OPTS} testid="select-ply" />
                  <DepthSelect label="Backing board" k="backing_depth_mm" form={form} onChange={onChange} options={BACKING_OPTS} testid="select-backing" />
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Spacer type</label>
                    <Select value={form.spacer_type} onValueChange={(v) => { onChange("spacer_type", v); const opts = SPACER_DEPTH_OPTS[v]; onChange("spacer_depth_mm", opts ? String(opts[0].mm) : "0"); }}>
                      <SelectTrigger data-testid="select-spacer-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SPACER_TYPE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.spacer_type !== "none" && (
                    <DepthSelect label="Spacer thickness" k="spacer_depth_mm" form={form} onChange={onChange} options={SPACER_DEPTH_OPTS[form.spacer_type] || []} testid="select-spacer" />
                  )}
                </div>
              )}

              {isSubscribed ? (
                <CostInputs form={form} onChange={onChange} result={result} unit={unit} />
              ) : (
                <div className="mt-6 rounded-xl border border-dashed border-border bg-secondary/40 p-5" data-testid="cost-locked">
                  <h2 className="flex items-center gap-1.5 font-heading text-base font-bold text-muted-foreground">
                    <Lock className="h-4 w-4" /> Materials cost calculator
                  </h2>
                  <p className="mt-2 text-xs text-muted-foreground">Cost every component from your cut sizes and see your total framing cost. Available with a subscription.</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={goPricing} data-testid="cost-locked-cta">
                    {isTrial ? "Subscribe to unlock" : "See plans"}
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button data-testid="open-save-dialog" className="min-w-[120px] flex-1" onClick={onSaveClick}><Save className="mr-2 h-4 w-4" /> Save</Button>
                <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Save calculation</DialogTitle>
                      <DialogDescription>Give this frame setup a name to find it later in your saved list.</DialogDescription>
                    </DialogHeader>
                    <Input data-testid="input-save-name" placeholder="e.g. Smith commission — A2 print" value={saveName} onChange={(e) => setSaveName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSave()} />
                    <DialogFooter><Button data-testid="confirm-save" onClick={() => doSave()}>Save</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                <Dialog open={dupOpen} onOpenChange={setDupOpen}>
                  <DialogContent data-testid="duplicate-name-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading">A project with this name already exists</DialogTitle>
                      <DialogDescription>You already have a saved project called “{saveName.trim()}”. Overwrite it with the current setup, or save under a different name?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                      <Button variant="outline" onClick={() => { setDupOpen(false); setSaveOpen(true); }} data-testid="dup-rename">Save with a different name</Button>
                      <Button onClick={() => doSave(true)} data-testid="dup-overwrite">Overwrite existing</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button data-testid="print-btn" variant="outline" onClick={onPrint}><Printer className="mr-2 h-4 w-4" /> Cut sheet</Button>
                <Button data-testid="export-pdf-btn" variant="outline" onClick={exportPdf}><FileDown className="mr-2 h-4 w-4" /> Export</Button>
                <Button data-testid="reset-btn" variant="outline" size="icon" onClick={resetForm}><RotateCcw className="h-4 w-4" /></Button>
              </div>
            </div>
          </section>

          {/* Preview + results */}
          <section className="lg:col-span-7 xl:col-span-8">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
              <div className="space-y-6 xl:col-span-3">
                <div className="aspect-[4/5] w-full overflow-hidden rounded-xl border border-border bg-card sm:aspect-square">
                  <FramePreview r={result} unit={unit} mouldingColor={form.moulding_color} matColors={[form.mat_color_1, form.mat_color_2, form.mat_color_3]} artworkUrl={form.artwork_image_url ? `${process.env.REACT_APP_BACKEND_URL}${form.artwork_image_url}` : ""} />
                </div>
                <CrossSection r={result} unit={unit} />
              </div>

              <div className="xl:col-span-2">
                <div className="rounded-xl border border-border bg-card p-5">
                  <h2 className="mb-3 font-heading text-base font-bold">Results</h2>

                  {result?.warnings?.length > 0 && (
                    <div data-testid="result-warning" className="mb-3 space-y-2">
                      {result.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{w}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="mb-1 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">{solvingFrame ? "Artwork" : "Computed artwork"}</p>
                  <ResultRow label="Width × Height" testid="result-painting" value={`${fmt(result?.painting_width, unit)} × ${fmt(result?.painting_height, unit)}`} unit={unit} />

                  {layers > 0 && (
                    <>
                      <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Visible mat reveals</p>
                      <ResultRow label="Top" value={fmt(result?.mat_top, unit)} unit={unit} testid="result-mat-top" />
                      <ResultRow label="Bottom" value={fmt(result?.mat_bottom, unit)} unit={unit} testid="result-mat-bottom" />
                      <ResultRow label="Left" value={fmt(result?.mat_left, unit)} unit={unit} testid="result-mat-left" />
                      <ResultRow label="Right" value={fmt(result?.mat_right, unit)} unit={unit} testid="result-mat-right" />
                    </>
                  )}

                  <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Cut sizes</p>
                  <ResultRow label="Outer frame" testid="result-outer-frame-cut"
                    help="2 pieces each, size refers to outside of moulding, mitred inward to 45 degrees"
                    value={`${fmt(result?.outer_width, unit)} × ${fmt(result?.outer_height, unit)}`} unit={unit} />

                  {result?.spacer_type && result.spacer_type !== "none" && (
                    <>
                      {(result.spacer_type === "matboard" || result.spacer_type === "foam") && (
                        <>
                          <ResultRow label="Spacer outer" testid="result-spacer-outer" value={`${fmt(result?.spacer_outer_width, unit)} × ${fmt(result?.spacer_outer_height, unit)}`} unit={unit} />
                          <ResultRow label="Spacer inner" testid="result-spacer-inner"
                            help="Cut to the matboard (rebate) size with a border no wider than half the rebate width, so the spacer stays hidden behind the glass."
                            value={`${fmt(result?.spacer_inner_width, unit)} × ${fmt(result?.spacer_inner_height, unit)}`} unit={unit} />
                        </>
                      )}
                      {result.spacer_type === "plastic" && (
                        <>
                          <ResultRow label="Spacer top/bottom (×2)" testid="result-spacer-tb" value={fmt(result?.spacer_tb_length, unit)} unit={unit} />
                          <ResultRow label="Spacer left/right (×2)" testid="result-spacer-lr"
                            help="Left/right strips are shortened by two 4 mm spacer widths so they fit between the top and bottom strips."
                            value={fmt(result?.spacer_lr_length, unit)} unit={unit} />
                        </>
                      )}
                      {result.spacer_type === "wood" && (
                        <ResultRow label="Spacer lengths" testid="result-spacer-wood"
                          help="2 pieces each, mitre-cut inward to 45°. Spacer lengths should match the glass/acrylic lengths so they sit just inside the frame without bowing."
                          value={`${fmt(result?.spacer_wood_length_a, unit)} × ${fmt(result?.spacer_wood_length_b, unit)}`} unit={unit} />
                      )}
                    </>
                  )}

                  <ResultRow label="Glazing" testid="result-glass"
                    value={`${fmt(result?.glass_cut_width, unit)} × ${fmt(result?.glass_cut_height, unit)}`} unit={unit} />
                  {layers > 0 && (
                    <ResultRow label="Matboard" testid="result-total-mat" value={`${fmt(result?.total_mat_width, unit)} × ${fmt(result?.total_mat_height, unit)}`} unit={unit} />
                  )}
                  <ResultRow label="Backing" testid="result-backing" value={`${fmt(result?.backing_cut_width, unit)} × ${fmt(result?.backing_cut_height, unit)}`} unit={unit} />

                  {mats.length > 0 && (
                    <>
                      <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                        Border cuts <span className="lowercase text-[11px]">(matboard edge → cut line)</span>
                      </p>
                      <div data-testid="border-cuts-table" className="grid gap-x-2 text-sm" style={{ gridTemplateColumns: `64px repeat(${mats.length}, 1fr)` }}>
                        <span></span>
                        {mats.map((m) => <span key={m.index} className="pb-1 text-right text-[11px] font-semibold text-muted-foreground">{m.name}</span>)}
                        {["top", "bottom", "left", "right"].map((side) => (
                          <React.Fragment key={side}>
                            <span className="border-t border-border py-1.5 capitalize text-muted-foreground">{side}</span>
                            {mats.map((m) => (
                              <span key={m.index} data-testid={`cut-${side}-${m.index}`} className="border-t border-border py-1.5 text-right font-mono">{fmt(m[`cut_${side}`], unit)}</span>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  )}

                  <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">{solvingFrame ? "Computed frame" : "Frame"}</p>
                  <ResultRow label="Outer frame" testid="result-outer" strong value={`${fmt(result?.outer_width, unit)} × ${fmt(result?.outer_height, unit)}`} unit={unit} />
                  <ResultRow label="Visible opening" testid="result-visible-opening" value={`${fmt(result?.visible_opening_width, unit)} × ${fmt(result?.visible_opening_height, unit)}`} unit={unit} />
                  <ResultRow label={`Moulding length (incl. ${fmt(result?.moulding_allowance_pct, unit)}% allowance)`} testid="result-moulding-length"
                    help="Total linear moulding to buy — the frame perimeter plus your cutting allowance for saw kerf and trial cuts. Handy for costing."
                    value={fmt(result?.moulding_length_total, unit)} unit={unit} />
                  {result?.depth_calc && (
                    <ResultRow label="Total stack depth" testid="result-depth" value={fmt(result?.total_depth, unit)} unit={unit} />
                  )}

                  {isSubscribed && costData.rows.length > 0 && (
                    <>
                      <p className="mb-1 mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">Component costs</p>
                      {costData.rows.map((row) => (
                        <ResultRow key={row.key} label={row.label} testid={`result-cost-${row.key}`} value={fmtMoney(row.cost, costData.currency)} unit="" />
                      ))}
                      <ResultRow label="Total framing cost" testid="result-cost-total" strong value={fmtMoney(costData.total, costData.currency)} unit="" />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-heading text-base font-bold">Saved calculations</h2>
                {isSubscribed && (
                  <div className="flex items-center gap-2">
                    <div className="relative sm:w-64">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="search-saved" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by reference…" className="pl-9" />
                    </div>
                    <Button variant="outline" size="sm" onClick={exportLibrary} data-testid="export-library-btn" title="Export all saved projects to CSV">
                      <FileDown className="mr-1.5 h-4 w-4" /> Export all
                    </Button>
                  </div>
                )}
              </div>
              {!user ? (
                <div className="rounded-lg bg-secondary/60 p-6 text-center text-sm text-muted-foreground" data-testid="saved-login-prompt">
                  <p><button className="font-semibold text-primary hover:underline" onClick={goTrial}>Start your free 7-day trial</button> to save your projects and reload them any time.</p>
                </div>
              ) : (
                <SavedCalculations items={filteredSaved} onLoad={onLoad} onDelete={onDelete} onDuplicate={onDuplicate} canDuplicate={isSubscribed} query={q} />
              )}
            </div>
          </section>
        </main>

        <CutSheet r={result} unit={unit} reference={form.reference} />

        <Dialog open={softOpen} onOpenChange={setSoftOpen}>
          <DialogContent data-testid="soft-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">We see you're enjoying the calculator</DialogTitle>
              <DialogDescription>
                Start your 7-day free trial to save your progress, continue refining and access precise cut sheets.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setSoftOpen(false)} data-testid="soft-prompt-dismiss">Keep exploring</Button>
              <Button onClick={goTrial} data-testid="soft-prompt-cta">Start free trial</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
